/**
 * What a coach's save of one exercise changes, worked out once for both save routes (#6428).
 *
 * The exercise settings page is opened from Edit Workouts and from the Training Overview (and the
 * coach check-up's Training tab, which shares that route). Both routes call this module, so what
 * a coach's change does to the plan, and what it records, cannot drift apart between them.
 *
 * It always compares the exercise AS THE PAGE OPENED IT with the exercise AS THE COACH LEFT IT.
 * It never compares against what training wrote meanwhile, so a client's own progression is never
 * recorded as a coach change.
 *
 * Example. Alex's Bench Press, one target of 8, next weight 77.5 kg. His coach lowers it to
 * 72.5 kg. `prepareChange` answers: one change `target_weight_kg 77.5 -> 72.5`; the deload rule
 * for that day (`orgTarget_weight_kg` 72.5, remembered strength 94.9 kg from his latest 77.5 x 7);
 * and the cap-skip mark for that day. The caller writes those onto the sub-slot it saves with
 * `applyPreparedChange`, which also stamps `coachChange_utc`.
 *
 * Pure: no Angular, no database. The service around it is `coach-exercise-change.service.ts`;
 * the rules are in `design-docs/coach-exercise-editing.md`.
 */

import { IntensityLevel, pathAtLevel, Plan_SubSlot } from '@cf-shared/data-model/fitness-plan';
import { ExerciseStat } from '@cf-shared/data-model/user-state';
import { Log_WorkoutExercise, Log_WorkoutSet } from '@cf-shared/data-model/log-workout';
import { ExerciseChangeAuthor, ExerciseChangeDoc, ExerciseChangeEntry, ExerciseChangeField } from '@cf-shared/data-model/exercise-change';
import { deloadMemoryAfterHandWeightChange, IntensityScheme, schemeOfLevel, schemeOfPaths } from 'pages/exercise-configuration/scheme-switch';

/** one side of the comparison: the program entry and the two stats it can touch */
export interface CoachChangeSide {
  subSlot: Plan_SubSlot;
  /** the stat of the sub-slot's exercise */
  stat: ExerciseStat | undefined;
  /** the stat of `subSlot.setAsideExerciseId`, when one is recorded; Bring Back Now clears its quarantine */
  setAsideStat: ExerciseStat | undefined;
}

/** what `prepareChange` needs */
export interface PrepareChangeInput {
  subSlotId: string;
  original: CoachChangeSide;
  edited: CoachChangeSide;
  /** true when a scheme switch (or Calculate Weights) built the paths in this visit */
  pathsRebuiltBySwitch: boolean;
  /** the Sets count the page showed and the count left, when the person changed it (the page's own record) */
  volume?: { before: number; after: number };
  /** this exercise's entry in each recent workout, newest first; needed only when `deloadRuleApplies` */
  historyNewestFirst: readonly (Log_WorkoutExercise | null | undefined)[] | undefined;
  bodyWeight_kg: number;
  exerciseBw_pc: number;
  /** the one timestamp the caller chose for the whole save */
  changed_utc: string;
  author: ExerciseChangeAuthor;
}

/** the deload rule's result for one day whose Next Weight the coach changed */
export interface DeloadRuleOutcome {
  level: IntensityLevel;
  orgTarget_weight_kg: number;
  /** undefined: no session of that day was found, so the remembered strength stays as it was */
  lastDeload1rm_net_kg: number | undefined;
}

/** what the caller writes, and what it records */
export interface PreparedChange {
  deloadRule: DeloadRuleOutcome[];
  /** the days that get `coachSetWeight` (the one-session cap skip) */
  coachSetWeightLevels: IntensityLevel[];
  changes: ExerciseChangeEntry[];
  /** undefined when nothing changed: no stamp, no document */
  doc: ExerciseChangeDoc | undefined;
}

const levelsOf = (subSlot: Plan_SubSlot | undefined): IntensityLevel[] =>
  (subSlot?.exercisePaths ?? []).filter(path => !!path).map(path => path.intensityLevel).sort((a, b) => a - b);

/** a finite number, or undefined for anything else (a damaged or old-shaped document) */
const finite = (value: unknown): number | undefined => (typeof value === 'number' && Number.isFinite(value) ? value : undefined);

/**
 * The share of the lift the client's own body carries, as the logged sets recorded it. Each set
 * stores its own `bw_pc`, and the deload rule reads that first, so this is only the fallback for a
 * set that has none; 0 when no set says. Both save routes use it.
 */
export function bodyWeightShareFromSessions(sessionsNewestFirst: readonly (readonly Log_WorkoutSet[])[] | undefined): number {
  for (const sets of sessionsNewestFirst ?? []) {
    const found = sets.find(set => Number.isFinite(set?.stats?.bw_pc))?.stats?.bw_pc;
    if (found !== undefined) return found;
  }
  return 0;
}

/**
 * The scheme a side ends up with: a pending rebuild (the strength-test fallback of a switch) decides
 * it, because the paths still on the side are the old ones the rebuild will wipe.
 */
function schemeOfSide(subSlot: Plan_SubSlot): IntensityScheme {
  return subSlot.rebuildPathsForIntensity !== undefined ? schemeOfLevel(subSlot.rebuildPathsForIntensity) : schemeOfPaths(subSlot.exercisePaths);
}

/**
 * True when a changed Next Weight counts as a deload: the same exercise and the same days on both
 * sides, and no scheme switch built the paths in this visit. A round trip DUP -> One Target -> DUP
 * ends with the original days but fresh paths, which is why the flag is asked and not only the days.
 */
export function deloadRuleApplies(original: Plan_SubSlot, edited: Plan_SubSlot, pathsRebuiltBySwitch: boolean): boolean {
  if (pathsRebuiltBySwitch || original?.exercise_id !== edited?.exercise_id) return false;
  const before = levelsOf(original);
  const after = levelsOf(edited);
  return before.length === after.length && before.every((level, i) => level === after[i]);
}

/** the days whose Next Weight differs between the two sides, when the deload rule applies */
export function levelsWithChangedWeight(original: Plan_SubSlot, edited: Plan_SubSlot): IntensityLevel[] {
  return levelsOf(edited).filter(level =>
    finite(pathAtLevel(original, level)?.target_weight_kg) !== finite(pathAtLevel(edited, level)?.target_weight_kg));
}

/** Works out everything a coach's save of one sub-slot changes. Pure and synchronous. */
export function prepareChange(input: PrepareChangeInput): PreparedChange {
  const { original, edited } = input;
  const changes: ExerciseChangeEntry[] = [];
  const deloadRule: DeloadRuleOutcome[] = [];
  let coachSetWeightLevels: IntensityLevel[] = [];

  // a different exercise in the slot is Edit Workouts' own switch, not this page's change
  if (original.subSlot?.exercise_id !== edited.subSlot?.exercise_id) {
    return { deloadRule, coachSetWeightLevels, changes, doc: undefined };
  }

  const schemeBefore = schemeOfSide(original.subSlot);
  const schemeAfter = schemeOfSide(edited.subSlot);
  // a switch that fell back to a strength test keeps the old days until the next load wipes them
  const strengthTestPending = edited.subSlot.rebuildPathsForIntensity !== undefined;
  if (input.pathsRebuiltBySwitch || schemeBefore !== schemeAfter) {
    changes.push({ field: ExerciseChangeField.Scheme, before: schemeBefore, after: schemeAfter });
  }

  // per day: the rep target, the next weight and the next minimum reps
  const allLevels = [...new Set([...levelsOf(original.subSlot), ...levelsOf(edited.subSlot)])].sort((a, b) => b - a);
  for (const level of allLevels) {
    const before = pathAtLevel(original.subSlot, level);
    const after = pathAtLevel(edited.subSlot, level);
    const pairs: Array<[ExerciseChangeField, number | undefined, number | undefined]> = [
      [ExerciseChangeField.TargetReps, finite(before?.target_reps), finite(after?.target_reps)],
      [ExerciseChangeField.TargetWeight, finite(before?.target_weight_kg), finite(after?.target_weight_kg)],
      [ExerciseChangeField.MinReps, finite(before?.min_reps), finite(after?.min_reps)],
    ];
    for (const [field, b, a] of pairs) {
      // a day that only exists on the old side is reported once, by the scheme entry; days a pending
      // strength test will rebuild say nothing about the coach's choice
      if (b !== a && after && !strengthTestPending) {
        changes.push({ field, level, before: b, after: a });
      }
    }
  }

  // the count the person saw and left, as the page recorded it: leaving the field rewrites the
  // stored base even when the count stays, so the stored documents cannot answer this
  if (input.volume && input.volume.before !== input.volume.after) {
    changes.push({ field: ExerciseChangeField.Volume, before: input.volume.before, after: input.volume.after });
  }
  if (original.stat?.preferMyoReps !== edited.stat?.preferMyoReps) {
    changes.push({ field: ExerciseChangeField.Myo, before: original.stat?.preferMyoReps, after: edited.stat?.preferMyoReps });
  }
  if (original.stat?.preferRMTesting !== edited.stat?.preferRMTesting) {
    changes.push({ field: ExerciseChangeField.RmTesting, before: original.stat?.preferRMTesting, after: edited.stat?.preferRMTesting });
  }
  if (ExerciseStat.hasQuarantineRecorded(original.setAsideStat) && !ExerciseStat.hasQuarantineRecorded(edited.setAsideStat)) {
    changes.push({ field: ExerciseChangeField.SetAsideCleared, before: original.subSlot.setAsideExerciseId });
  }
  // the rare case the page also offers: the exercise in the slot is itself set aside
  if (ExerciseStat.hasQuarantineRecorded(original.stat) && !ExerciseStat.hasQuarantineRecorded(edited.stat)) {
    changes.push({ field: ExerciseChangeField.SetAsideCleared, before: original.subSlot.exercise_id });
  }
  if (original.subSlot.plateauWorkoutLog_id && !edited.subSlot.plateauWorkoutLog_id) {
    changes.push({ field: ExerciseChangeField.PlateauAnswered, before: original.subSlot.plateauWorkoutLog_id });
  }
  const openVolumeQuestion = (subSlot: Plan_SubSlot) =>
    (subSlot.exercisePaths ?? []).some(path => typeof path?.autoregulationWorkoutLog_id === 'string');
  if (openVolumeQuestion(original.subSlot) && !openVolumeQuestion(edited.subSlot)) {
    changes.push({ field: ExerciseChangeField.VolumeQuestionAnswered });
  }

  // the deload rule and the cap-skip mark. this module is the ONLY writer of the mark: a day is
  // marked when its weight differs original -> edited, every day when the paths were rebuilt, so a
  // weight typed back to its original value gets no mark
  if (strengthTestPending) {
    // the next load replaces these days with a strength test: no weight of the coach's to protect
    coachSetWeightLevels = [];
  } else if (input.pathsRebuiltBySwitch) {
    coachSetWeightLevels = levelsOf(edited.subSlot);
  } else if (deloadRuleApplies(original.subSlot, edited.subSlot, false)) {
    coachSetWeightLevels = levelsWithChangedWeight(original.subSlot, edited.subSlot);
    for (const level of coachSetWeightLevels) {
      const weight = finite(pathAtLevel(edited.subSlot, level)?.target_weight_kg);
      if (weight === undefined) continue;
      deloadRule.push({
        level,
        orgTarget_weight_kg: weight,
        lastDeload1rm_net_kg: deloadMemoryAfterHandWeightChange(input.historyNewestFirst ?? [], level, input.bodyWeight_kg, input.exerciseBw_pc),
      });
    }
  }

  const doc: ExerciseChangeDoc | undefined = changes.length === 0 ? undefined : {
    subSlotId: input.subSlotId,
    exercise_id: edited.subSlot.exercise_id,
    changed_utc: input.changed_utc,
    author: input.author,
    changes,
  };
  return { deloadRule, coachSetWeightLevels, changes, doc };
}

/**
 * Writes `prepareChange`'s result onto the sub-slot the caller is about to save: the deload rule,
 * the cap-skip marks, and the stamp `coachChange_utc` when anything changed. Synchronous, so the
 * Training Overview route can call it between reloading the documents and committing them.
 */
export function applyPreparedChange(subSlot: Plan_SubSlot, prepared: PreparedChange): void {
  for (const outcome of prepared.deloadRule) {
    const path = pathAtLevel(subSlot, outcome.level);
    if (!path) continue;
    path.orgTarget_weight_kg = outcome.orgTarget_weight_kg;
    if (outcome.lastDeload1rm_net_kg !== undefined) {
      path.lastDeload1rm_net_kg = outcome.lastDeload1rm_net_kg;
    }
  }
  for (const level of prepared.coachSetWeightLevels) {
    const path = pathAtLevel(subSlot, level);
    if (path) path.coachSetWeight = true;
  }
  if (prepared.doc) {
    subSlot.coachChange_utc = prepared.doc.changed_utc;
  }
}
